import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Money } from "@/components/wallet/shared";

export function useWalletItems() {
  return useQuery({ queryKey: ["wallet-items"], queryFn: async () => (await api.get("/wallet/items")).data });
}

export default function ItemsDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data: items = [] } = useWalletItems();
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const inval = () => qc.invalidateQueries({ queryKey: ["wallet-items"] });
  const onErr = (e) => toast.error(e.response?.data?.detail || "Errore");
  const add = useMutation({
    mutationFn: async () => (await api.post("/wallet/items", { name, price: parseFloat(price) })).data,
    onSuccess: () => { toast.success("Voce aggiunta"); setName(""); setPrice(""); inval(); }, onError: onErr,
  });
  const upd = useMutation({ mutationFn: async (it) => (await api.patch(`/wallet/items/${it.id}`, it)).data, onSuccess: inval, onError: onErr });
  const del = useMutation({ mutationFn: async (id) => (await api.delete(`/wallet/items/${id}`)).data, onSuccess: inval, onError: onErr });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Listino voci di spesa</DialogTitle>
          <DialogDescription>Crea le voci (es. caffè, pasto, libro) con il prezzo predefinito da usare negli addebiti.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome voce" data-testid="item-new-name"/>
          <Input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="€" className="w-28" data-testid="item-new-price"/>
          <Button className="bg-[#0F766E] hover:bg-[#115E59]" disabled={!name.trim() || price === "" || add.isPending} onClick={() => add.mutate()} data-testid="item-add"><Plus size={14}/></Button>
        </div>
        <ul className="divide-y divide-stone-100" data-testid="items-list">
          {items.length === 0 && <li className="py-6 text-center text-sm text-stone-400">Nessuna voce nel listino.</li>}
          {items.map((it) => (
            <li key={it.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`item-${it.id}`}>
              <span className={`flex-1 ${it.active ? "" : "text-stone-400 line-through"}`}>{it.name}</span>
              <Money value={it.price} className="font-medium"/>
              <Switch checked={it.active} onCheckedChange={(v) => upd.mutate({ ...it, active: v })} data-testid={`item-active-${it.id}`}/>
              <button type="button" onClick={() => { if (window.confirm(`Eliminare "${it.name}"?`)) del.mutate(it.id); }} className="text-stone-400 hover:text-red-600" data-testid={`item-del-${it.id}`}><Trash2 size={14}/></button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
